import {
  formatMonthYearShort,
  isCompleteMonthYear,
  parseIsoToMonthYear,
} from '@/lib/month-year'

/** Case-insensitive match for skill strings on the profile (claimed vs experience/education/cert lists). */
export function matchesProfileSkillName(skillName: string, candidate?: string | null): boolean {
  return candidate?.trim().toLowerCase() === skillName.trim().toLowerCase()
}

/** Claimed skill list with every entry matching skillName removed (trimmed, case-insensitive). */
export function filterOutMatchingClaimedSkill(skills: string[] | undefined, skillName: string): string[] {
  return (skills ?? []).filter((s) => !matchesProfileSkillName(skillName, s))
}

/** Claimed skill list where every entry matching `fromSkillName` is replaced by `toTrimmedName`. */
export function renameMatchingClaimedSkill(
  skills: string[] | undefined,
  fromSkillName: string,
  toTrimmedName: string
): string[] {
  return (skills ?? []).map((s) => (matchesProfileSkillName(fromSkillName, s) ? toTrimmedName : s))
}

/** Canonical claimed label matching `skillKey` (case-insensitive), if present. */
export function resolveCanonicalClaimedSkill(
  skills: string[] | undefined,
  skillKey: string
): string | undefined {
  const key = skillKey.trim()
  if (!key) return undefined
  return skills?.find((s) => matchesProfileSkillName(skillKey, s))
}

export type PrepareAppendClaimedSkillResult =
  | { ok: true; skills: string[]; trimmed: string }
  | { ok: false; error: string }

/** Validates append for claimed skills (dedupe case-insensitive). */
export function prepareAppendClaimedSkill(
  skills: string[] | undefined,
  rawName: string
): PrepareAppendClaimedSkillResult {
  const trimmed = rawName.trim()
  if (!trimmed) return { ok: false, error: 'Enter a skill name.' }
  const list = skills ?? []
  if (list.some((s) => matchesProfileSkillName(trimmed, s))) {
    return { ok: false, error: `"${trimmed}" is already in your skills.` }
  }
  return { ok: true, skills: [...list, trimmed], trimmed }
}

export type PrepareRenameClaimedSkillResult =
  | { ok: true; skills: string[]; trimmed: string }
  | { ok: false; error: string }

/** Validates rename for claimed skills (duplicate check excludes matching rows). Caller should skip when draft unchanged. */
export function prepareRenameClaimedSkill(
  skills: string[] | undefined,
  skillKey: string,
  rawNewName: string
): PrepareRenameClaimedSkillResult {
  const canonical = resolveCanonicalClaimedSkill(skills, skillKey)
  if (!canonical) return { ok: false, error: 'Skill not found on profile.' }
  const trimmed = rawNewName.trim()
  if (!trimmed) return { ok: false, error: 'Enter a skill name.' }
  if (trimmed === canonical.trim()) return { ok: false, error: 'No changes to save.' }
  const others = filterOutMatchingClaimedSkill(skills, skillKey)
  if (others.some((s) => matchesProfileSkillName(trimmed, s))) {
    return { ok: false, error: `"${trimmed}" is already in your skills.` }
  }
  return { ok: true, skills: renameMatchingClaimedSkill(skills, skillKey, trimmed), trimmed }
}

/** Friendly month-year for a stored ISO date (first of month). */
export function formatProfileMonthYear(iso?: string | null): string | undefined {
  const parsed = parseIsoToMonthYear(iso)
  if (!isCompleteMonthYear(parsed)) return undefined
  return formatMonthYearShort(parsed)
}

/** Education / experience date ranges in profile evidence UI. */
export function formatProfileEvidenceDateRange(start?: string, end?: string | null): string {
  const startLabel = formatProfileMonthYear(start)
  if (!startLabel) return 'Unknown'
  const endLabel = end ? (formatProfileMonthYear(end) ?? 'Unknown') : 'Present'
  return `${startLabel} – ${endLabel}`
}

/** Certification issued / expiry line. */
export function formatProfileCertificationDates(
  issued?: string | null,
  expires?: string | null,
): string | undefined {
  if (!issued && !expires) return undefined
  const issuedLabel = formatProfileMonthYear(issued) ?? 'unknown'
  const expiresLabel = expires
    ? `Expires ${formatProfileMonthYear(expires) ?? 'unknown'}`
    : 'Does not expire'
  return `Issued ${issuedLabel} • ${expiresLabel}`
}

export function formatProfilePaperMeta(paper: {
  authors?: string | null
  venue?: string | null
  year?: number | null
  doi?: string | null
}): string | undefined {
  const parts = [
    paper.authors?.trim() || null,
    paper.venue?.trim() || null,
    paper.year != null ? String(paper.year) : null,
    paper.doi?.trim() ? `DOI ${paper.doi.trim()}` : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' • ') : undefined
}

export type SkillEvidenceProfileSlice = {
  experiences?: { skills?: string[] }[]
  education?: { related_skills?: string[] }[]
  certifications?: { related_skills?: string[] }[]
}

export type SkillEvidenceReferenceCounts = {
  experiences: number
  education: number
  certifications: number
}

/** Count experience, education, and certification rows referencing a skill (case-insensitive). */
export function countSkillEvidenceReferences(
  profile: SkillEvidenceProfileSlice | null | undefined,
  skillName: string
): SkillEvidenceReferenceCounts {
  if (!profile) {
    return { experiences: 0, education: 0, certifications: 0 }
  }
  const experiences = (profile.experiences ?? []).filter((row) =>
    (row.skills ?? []).some((skill) => matchesProfileSkillName(skillName, skill))
  ).length
  const education = (profile.education ?? []).filter((row) =>
    (row.related_skills ?? []).some((skill) => matchesProfileSkillName(skillName, skill))
  ).length
  const certifications = (profile.certifications ?? []).filter((row) =>
    (row.related_skills ?? []).some((skill) => matchesProfileSkillName(skillName, skill))
  ).length
  return { experiences, education, certifications }
}

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`
}

/** Human-readable cascade impact for remove/rename confirmations. */
export function formatSkillCascadeImpactMessage(
  skillLabel: string,
  counts: SkillEvidenceReferenceCounts,
  action: 'remove' | 'rename'
): string {
  const parts: string[] = []
  if (counts.experiences > 0) {
    parts.push(pluralize(counts.experiences, 'role', 'roles'))
  }
  if (counts.education > 0) {
    parts.push(pluralize(counts.education, 'education entry', 'education entries'))
  }
  if (counts.certifications > 0) {
    parts.push(pluralize(counts.certifications, 'certification', 'certifications'))
  }

  if (action === 'remove') {
    if (parts.length === 0) {
      return `Remove ${skillLabel} from your profile?`
    }
    return `Remove ${skillLabel} from your profile? It will also be removed from ${parts.join(', ')}.`
  }

  if (parts.length === 0) {
    return `Rename ${skillLabel} on your profile?`
  }
  return `Rename ${skillLabel}? This updates ${parts.join(', ')} that reference this skill.`
}

/** Tailwind classes for top-skill / summary proficiency pill (Expert / Advanced / Working / default). */
export function proficiencyLabelClasses(label: string): string {
  switch (label) {
    case 'Expert':
      return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-300'
    case 'Advanced':
      return 'border-purple-500/30 bg-purple-500/10 text-purple-700 dark:text-purple-300'
    case 'Working':
      return 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300'
    default:
      return 'border-border bg-background text-foreground'
  }
}

/** Dot fill for inline proficiency indicator (Expert / Advanced / Working / default). */
export function proficiencyDotClasses(label: string): string {
  switch (label) {
    case 'Expert':
      return 'bg-yellow-500'
    case 'Advanced':
      return 'bg-purple-500'
    case 'Working':
      return 'bg-blue-500'
    default:
      return 'bg-muted-foreground'
  }
}
